import styled from '@emotion/styled'
import Image from 'next/image'
import { useContext, useEffect, useRef } from 'react'
import { AnalyticsContext } from '../../../../context/analyticsContext'
import { MEDIA_BORDER_RADIUS_VAR } from '../../../../context/CustomizationContextProvider'
import {
    FullScreenVideoContext,
    StorefrontPlaylist,
} from '../../../../context/fullScreenVideoContext'
import { VideoContext } from '../../../../context/viddyVideoContext'
import { Constants } from '../../../../style/styleConstants'
import { usePrefersReducedMotion } from '../../../../hooks/usePrefersReducedMotion'
import { useInView } from '../../../../hooks/useInView'
import { getVideoPosterUrl } from '../../../../utils/helpers'

const PlayerContainer = styled.div({
    position: 'relative',
    width: '100%',
    borderRadius: `var(${MEDIA_BORDER_RADIUS_VAR})`,
    overflow: 'hidden',
})

const DEFAULT_ASPECT_RATIO = '9/16'

function toCssAspectRatio(muxRatio: string | null | undefined): string {
    if (!muxRatio) return DEFAULT_ASPECT_RATIO
    const parts = muxRatio.split(':')
    if (parts.length !== 2) return DEFAULT_ASPECT_RATIO
    const [w, h] = parts.map(Number)
    if (!w || !h) return DEFAULT_ASPECT_RATIO
    return `${w}/${h}`
}

function getMuxVideoUrl(playbackId: string): string {
    return `https://stream.mux.com/${playbackId}/low.mp4`
}

export const SingleVideoPlayer: React.FC<{
    playlist: StorefrontPlaylist
}> = ({ playlist }) => {
    const video = playlist.videos[0]
    const { setCurrentIdx, setCurrentPlaylist } = useContext(
        FullScreenVideoContext,
    )
    const { setIsMuted, setVideoTapped, videoTapped } = useContext(VideoContext)
    const { Analytics } = useContext(AnalyticsContext)
    const reducedMotion = usePrefersReducedMotion()
    const videoRef = useRef<HTMLVideoElement>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const isVisible = useInView(containerRef, { threshold: 0.3 })

    useEffect(() => {
        if (!videoRef.current) return
        if (isVisible && !reducedMotion) {
            videoRef.current.play().catch(() => {})
        } else {
            videoRef.current.pause()
        }
    }, [isVisible, reducedMotion])

    const onClick = () => {
        setCurrentPlaylist(playlist)
        setCurrentIdx(0)
        if (!videoTapped) {
            setVideoTapped(true)
            setIsMuted(false)
        }
        Analytics.TRACK_CAROUSEL_EXPAND(playlist, video.name, video.videoHash)
    }

    const playbackId = video.playbackId
    const posterSrc = getVideoPosterUrl(video)
    const aspectRatio = toCssAspectRatio(video.aspectRatio)

    return (
        <PlayerContainer
            ref={containerRef}
            onClick={onClick}
            style={{ aspectRatio }}
        >
            {playbackId ? (
                <video
                    ref={videoRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 'inherit',
                    }}
                    playsInline
                    muted
                    loop
                    preload="auto"
                    poster={posterSrc ?? undefined}
                    src={getMuxVideoUrl(playbackId)}
                />
            ) : posterSrc ? (
                <Image
                    sizes={`(max-width: ${Constants.EXPANDED_BREAKPOINT}px) 100vw, ${Constants.CONTENT_BLOCK_DESKTOP_WIDTH}px`}
                    priority
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: 'inherit',
                    }}
                    fill
                    src={posterSrc}
                    alt=""
                />
            ) : null}
        </PlayerContainer>
    )
}
